import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, useParams, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import slugify from './utils/slugify';
import { ThemeProvider } from './theme';
import { Box, Typography, Modal, Backdrop, useMediaQuery, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FlightLoader from './components/Loader/FlightLoader';
import TrainLoader from './components/Loader/TrainLoader';
import HotelLoader from './components/Loader/HotelLoader';
import ErrorBoundary from './components/ErrorBoundary';
import NotFoundPage from './components/common/NotFoundPage';
import { warmVerticalLoaderImages } from './components/Loader/utils/warmLoaderAssets';
import { trackGtmPageView } from './analytics/gtm';
import { trackMetaPageView } from './analytics/metaPixel';
import { POLICY_ROUTES } from './config/companyConfig';

// import Header from './components/Header';
// import NavigationHeader from './components/NavigationHeader';
import Chatbot from './components/Chatbot/Chatbot';
import ChatbotWrapper from './components/FloatingChatbot/ChatbotWrapper';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { SearchProvider } from './context/SearchContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginModal from './components/Auth/LoginModal';
import SignupModal from './components/Auth/SignupModal';
import AccountPage from './components/Auth/AccountPage';
// import FlightBookingPage from './components/FlightBookingPage';
// import FlightListings from './components/FlightListings/FlightListings';

warmVerticalLoaderImages();

const ROUTE_SUSPENSE_FALLBACK_SX = {
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#FBFBFB',
};

/** Vertical loader while React.lazy route chunks load (same route rules as former Lottie picker). */
function getRouteSuspenseFallback(pathname) {
  const path = pathname || '';

  if (path.includes('/train') || path.includes('/pnr-status')) {
    return (
      <Box sx={ROUTE_SUSPENSE_FALLBACK_SX}>
        <TrainLoader active minHeight="100vh" />
      </Box>
    );
  }
  if (path.includes('/flight')) {
    return (
      <Box sx={ROUTE_SUSPENSE_FALLBACK_SX}>
        <FlightLoader active />
      </Box>
    );
  }
  if (path.includes('/hotel')) {
    return (
      <Box sx={ROUTE_SUSPENSE_FALLBACK_SX}>
        <HotelLoader active minHeight="100vh" />
      </Box>
    );
  }

  const savedActiveForm = localStorage.getItem('yovo_active_form_type');
  if (savedActiveForm === 'train') {
    return (
      <Box sx={ROUTE_SUSPENSE_FALLBACK_SX}>
        <TrainLoader active minHeight="100vh" />
      </Box>
    );
  }
  if (savedActiveForm === 'hotel') {
    return (
      <Box sx={ROUTE_SUSPENSE_FALLBACK_SX}>
        <HotelLoader active minHeight="100vh" />
      </Box>
    );
  }

  return (
    <Box sx={ROUTE_SUSPENSE_FALLBACK_SX}>
      <FlightLoader active />
    </Box>
  );
}

// Lazy load all components except Chatbot
const Homepage = React.lazy(() => import('./components/Homepage/Homepage'));
// const AirportSearchDemo = React.lazy(() => import('./pages/AirportSearchDemo'));
// const Homepage2 = React.lazy(() => import('./components/Homepage/YovoTripHomepage'));
const FlightSelect = React.lazy(() => import('./components/flight_select/FlightSelect'));

const HotelSearchPage = React.lazy(() => import('./components/Hotels/pages/HotelSearchPage'));
const HotelDetailPage = React.lazy(() => import('./components/Hotels/pages/HotelDetailPage'));
const BookingPage = React.lazy(() => import('./components/Hotels/pages/BookingPage'))
const BookingReviewPage = React.lazy(() => import('./components/Hotels/pages/BookingReviewPage'));
const HotelBookingConfirmationPage = React.lazy(() => import('./components/Hotels/pages/HotelBookingConfirmationPage'));
const HotelCancellationReviewPage = React.lazy(() => import('./components/Hotels/pages/HotelCancellationReviewPage'));
const FlightFareCard = React.lazy(() => import('./components/flight_select/FlightFareCard'));
const FlightDetails = React.lazy(() => import('./components/flight_details/FlightDetails'));
const FlightBookingPageNew = React.lazy(() => import('./components/flight_details/FlightBookingPage'));
const SeatMapDemo = React.lazy(() => import('./components/flight_details/SeatMapDemo'));
const MyTrips = React.lazy(() => import('./components/flight_details/MyTrips'));
const SeatSelectionDemo = React.lazy(() => import('./components/flight_details/SeatSelectionDemo'));
const TrainSelect = React.lazy(() => import('./components/train_select/TrainSelect'));
const TrainResultsPage = React.lazy(() => import('./components/train_select/TrainResultsPage'));
const TrainDetailsPage = React.lazy(() => import('./components/train_select/TrainDetailsPage'));
const TrainSchedulePage = React.lazy(() => import('./components/train_select/TrainSchedulePage'));
const PNRStatusPage = React.lazy(() => import('./components/train_select/PNRStatusPage'));
const FlightSearchPage = React.lazy(() => import('./components/FlightSearch/FlightSearchPage'));
const FlightBookingPage = React.lazy(() => import('./components/FlightResults/FlightResults'));
// const FlightResultsDemo = React.lazy(() => import('./pages/FlightResultsDemo'));
const FlightSearchResults = React.lazy(() => import('./components/flight_select/FlightSearchResults'));
const FlightSearchResultsDemo = React.lazy(() => import('./pages/FlightSearchResultsDemo'));
const FlightCard = React.lazy(() => import('./components/flight_select/FlightDetailsPage'));
const FlightCardComponent = React.lazy(() => import('./components/flight_details/FlightCard'));
const CancellationPolicy = React.lazy(() => import('./components/flight_details/CancellationPolicy'));
const TravellerDetails = React.lazy(() => import('./components/flight_details/TravellerDetails'));
const Flight_SSR_Choose = React.lazy(() => import('./components/Flight_SSR/Flight_SSR_Choose'));
const Flight_Preview = React.lazy(() => import('./components/Flight_SSR/Flight_Preview'));
const PaymentPage = React.lazy(() => import('./components/Flight_Payment/PaymentPage'));
const BookingConfirmation = React.lazy(() => import('./components/Flight_Payment/BookingConfirmation'));
const ShortLinkRedirect = React.lazy(() => import('./components/common/ShortLinkRedirect'));
const ViewBookingDetails = React.lazy(() => import('./components/Flight_Payment/ViewBookingDetails'));
const TicketView = React.lazy(() => import('./components/Flight_Payment/TicketView'));
const FlightCancellation = React.lazy(() => import('./components/Flight_Payment/FlightCancellation'));
const CancellationReview = React.lazy(() => import('./components/Flight_Payment/CancellationReview'));
const CancelledBookingDetails = React.lazy(() => import('./components/Flight_Payment/CancelledBookingDetails'));
const ETicket = React.lazy(() => import('./components/Flight_Payment/E_Ticket'));
const SeatSelectionPage = React.lazy(() => import('./components/Flight_SSR/SeatSelectionPage'));
const TestAnimation = React.lazy(() => import('./components/TestAnimation'));
const IstanbulGuide = React.lazy(() => import('./components/Pages/IstanbulGuide'));
const HeritagePage = React.lazy(() => import('./components/Pages/Heritage_Place'));
const PrivacyPolicy = React.lazy(() => import('./components/Pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./components/Pages/TermsOfService'));
const CancellationRefundPolicy = React.lazy(() => import('./components/Pages/CancellationRefundPolicy'));
const Contact = React.lazy(() => import('./components/Pages/Contact'));
const FAQPage = React.lazy(() => import('./components/Pages/FAQPage'));

const FlightSeoSelect = React.lazy(() => import('./components/flight-seo/FlightSeoSelect'));

function HeritageLegacyRedirect() {
  const { placeName } = useParams();
  const decoded = decodeURIComponent(placeName || '');
  const slug = slugify(decoded) || decoded;
  return <Navigate to={`/destinations/heritage/${slug}`} replace />;
}
const TravelGallery = React.lazy(() => import('./components/TravelGallery/TravelGallery'));
const FlightFinancials = React.lazy(() => import('./components/Admin/FlightFinancials'));
const HotelFinancials = React.lazy(() => import('./components/Admin/HotelFinancials'));
const HotelPricingGovernance = React.lazy(() => import('./components/Admin/HotelPricingGovernance'));
const HotelPricingVersionCompare = React.lazy(() => import('./components/Admin/HotelPricingVersionCompare'));
const HotelPricingSimulator = React.lazy(() => import('./components/Admin/HotelPricingSimulator'));
const HotelBookingsConsole = React.lazy(() => import('./components/Admin/HotelBookingsConsole'));
const HotelRecon = React.lazy(() => import('./components/Admin/HotelRecon'));
const HotelOffers = React.lazy(() => import('./components/Admin/HotelOffers'));
const FlightBookingsConsole = React.lazy(() => import('./components/Admin/FlightBookingsConsole'));
const FlightPricingGovernance = React.lazy(() => import('./components/Admin/FlightPricingGovernance'));
const FlightRecon = React.lazy(() => import('./components/Admin/FlightRecon'));
const FlightOffers = React.lazy(() => import('./components/Admin/FlightOffers'));
const ServiceFeeManagement = React.lazy(() => import('./components/Admin/ServiceFeeManagement'));
const IssueMonitor = React.lazy(() => import('./components/Admin/IssueMonitor'));
const RefundManagement = React.lazy(() => import('./components/Admin/RefundManagement'));
const ServiceRequestManagement = React.lazy(() => import('./components/Admin/ServiceRequestManagement'));
const AdminLayout = React.lazy(() => import('./components/Admin/AdminLayout'));
const AdminPlaceholder = React.lazy(() => import('./components/Admin/AdminPlaceholder'));

const YovoHeader = React.lazy(() => import('./components/Homepage/YovoTripHeader'));
const FlightSearchHeader = React.lazy(() => import('./components/flight_select/FlightSearchHeader'));
const FlightDetailsAndFarePlans = React.lazy(() => import('./components/flight_select/FlightDetailsAndFarePlans'));

// Issue Monitor Page wrapper with auth protection
function IssueMonitorPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  React.useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.isAdmin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#F5F6FA' }}>
        <CircularProgress sx={{ color: '#181B28' }} />
      </Box>
    );
  }

  if (!isAuthenticated || !user?.isAdmin) {
    return null;
  }

  return (
    <Box sx={{ bgcolor: '#F5F6FA', minHeight: '100vh' }}>
      <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
        {React.createElement(
          React.lazy(() => import('./components/Header/YovoTripHeader')),
          { alwaysBrandBar: true }
        )}
      </React.Suspense>
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, pb: 4 }}>
        <IssueMonitor />
      </Box>
    </Box>
  );
}

// Refund Management Page wrapper with auth protection
function RefundManagementPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  React.useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.isAdmin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#F5F6FA' }}>
        <CircularProgress sx={{ color: '#181B28' }} />
      </Box>
    );
  }

  if (!isAuthenticated || !user?.isAdmin) {
    return null;
  }

  return (
    <Box sx={{ bgcolor: '#F5F6FA', minHeight: '100vh' }}>
      <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
        {React.createElement(
          React.lazy(() => import('./components/Header/YovoTripHeader')),
          { alwaysBrandBar: true }
        )}
      </React.Suspense>
      <RefundManagement />
    </Box>
  );
}

// P0-7B: Service Request Management Page wrapper with auth protection
function ServiceRequestManagementPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  React.useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.isAdmin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#F5F6FA' }}>
        <CircularProgress sx={{ color: '#181B28' }} />
      </Box>
    );
  }

  if (!isAuthenticated || !user?.isAdmin) {
    return null;
  }

  return (
    <Box sx={{ bgcolor: '#F5F6FA', minHeight: '100vh' }}>
      <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
        {React.createElement(
          React.lazy(() => import('./components/Header/YovoTripHeader')),
          { alwaysBrandBar: true }
        )}
      </React.Suspense>
      <ServiceRequestManagement />
    </Box>
  );
}

// Navigation wrapper component to use hooks inside Router
function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  /** Match MobileBottomNav: viewport width under MUI `sm` (600px) */
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  /** Same breakpoint as Homepage chatbot sidebar — `md` and up shows global chatbot UI */
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  /**
   * Revert: set `true` to show the bottom-right “Hey Its Yovo…” launcher + floating Chatbot on small screens again.
   */
  const SHOW_APP_CHATBOT_ON_MOBILE = false;
  const allowAppChatbot = SHOW_APP_CHATBOT_ON_MOBILE || isMdUp;
  const { isAuthenticated, loading } = useAuth();
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [flightData, setFlightData] = useState([]);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [farePlansModalOpen, setFarePlansModalOpen] = useState(false);

  // Disable browser scroll restoration - always start at top on navigation
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Scroll to top on every route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    trackMetaPageView(location.pathname, location.search);
    trackGtmPageView(location.pathname, location.search);
  }, [location.pathname, location.search]);

  // Determine active navigation item based on current route
  const getActiveNavItem = () => {
    if (location.pathname.includes('/flight')) return 'flight';
    if (location.pathname.includes('/train')) return 'trains';
    if (location.pathname.includes('/hotel') || location.pathname.includes('/stays')) return 'stays';
    return 'flight'; // default
  };

  // Handle navigation clicks
  const handleNavigationClick = (itemId) => {
    switch (itemId) {
      case 'flight':
        navigate('/flight');
        break;
      case 'trains':
        // Add train route when available
        break;
      case 'stays':
        // Add hotel route when available
        break;
      default:
        break;
    }
  };

  // Handle login click
  const handleLogin = () => {
    setLoginModalOpen(true);
  };

  // Handle signup click
  const handleSignup = () => {
    setSignupModalOpen(true);
  };

  // Switch from login to signup
  const handleSwitchToSignup = () => {
    setLoginModalOpen(false);
    setSignupModalOpen(true);
  };

  // Switch from signup to login
  const handleSwitchToLogin = () => {
    setSignupModalOpen(false);
    setLoginModalOpen(true);
  };

  // Expose the chatbot open function globally (no-op on mobile when allowAppChatbot is false)
  useEffect(() => {
    window.openChatbot = (initialMessage = '') => {
      if (!allowAppChatbot) return;
      setIsChatbotOpen(true);
      // If there's an initial message, we can store it for the chatbot to use
      if (initialMessage) {
        window.chatbotInitialMessage = initialMessage;
      }
    };
    // Expose function to set flight data
    window.setFlightDataForChatbot = setFlightData;
    // Expose function to open fare plans modal
    window.openFarePlans = () => {
      setFarePlansModalOpen(true);
    };
    return () => {
      delete window.openChatbot;
      delete window.setFlightDataForChatbot;
      delete window.chatbotInitialMessage;
      delete window.openFarePlans;
    };
  }, [allowAppChatbot]);

  useEffect(() => {
    if (!allowAppChatbot && isChatbotOpen) {
      setIsChatbotOpen(false);
    }
  }, [allowAppChatbot, isChatbotOpen]);

  const shouldShowChatbot = isChatbotOpen && allowAppChatbot;
  const pathNorm = (location.pathname || '/').replace(/\/+$/, '') || '/';
  const isProfileAccountRoute = pathNorm === '/account';
  const isHomepageRoute = ['/', '/flights', '/hotels', '/trains'].includes(pathNorm);
  const showFloatingYovoBar =
    allowAppChatbot &&
    !isChatbotOpen &&
    !isHomepageRoute &&
    !(isProfileAccountRoute && isMobileViewport);
  
  return (
    <>
      {/* Navigation Header */}
      {/* <NavigationHeader
        activeNavItem={getActiveNavItem()}
        onNavigationClick={handleNavigationClick}
        onLoginClick={handleLogin}
        onSignupClick={handleSignup}
        onAccountClick={handleAccountClick}
      /> */}

      {/* Auth Modals */}
      <LoginModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        onSwitchToSignup={handleSwitchToSignup}
      />
      <SignupModal
        open={signupModalOpen}
        onClose={() => setSignupModalOpen(false)}
        onSwitchToLogin={handleSwitchToLogin}
      />

      {/* Flight Details and Fare Plans Modal */}
      <Modal
        open={farePlansModalOpen}
        onClose={() => setFarePlansModalOpen(false)}
        disableScrollLock
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          }
        }}
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: { xs: 'center', md: 'center' },
          p: { xs: 0, md: 2 },
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            outline: 'none',
            maxWidth: { xs: '100%', md: '1040px' },
            width: '100%',
            maxHeight: { xs: '100dvh', md: '90vh' },
            height: { xs: '100%', md: 'auto' },
            minHeight: { xs: '100%', md: 'auto' },
            overflow: { xs: 'hidden', md: 'auto' },
            borderRadius: { xs: 0, md: 1 },
            flex: { xs: '1 1 auto', md: '0 1 auto' },
            alignSelf: { xs: 'stretch', md: 'auto' },
            pt: { xs: 'env(safe-area-inset-top, 0px)', md: 0 },
            pb: { xs: 'env(safe-area-inset-bottom, 0px)', md: 0 },
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}
        >
          <FlightDetailsAndFarePlans 
            onClose={() => setFarePlansModalOpen(false)}
            tripType="roundtrip"
          />
        </Box>
      </Modal>

      {/* Main Content */}
      <ErrorBoundary>
        <Suspense fallback={getRouteSuspenseFallback(location.pathname)}>
          <Routes>
            <Route path="/" element={<Homepage />} />
            <Route path="/flights" element={<Homepage />} />
            <Route path="/flights/:routeSlug" element={<FlightSeoSelect />} />
            <Route path="/hotels" element={<Homepage />} />
            <Route path="/trains" element={<Homepage />} />
            <Route path="/hotel" element={<HotelSearchPage />} />
            <Route path="/hotel/:hotelId" element={<HotelDetailPage />} />
            <Route path="/hotel/:hotelId/book/:roomId" element={<BookingPage />} />
            <Route path="/hotel/:hotelId/book/:roomId/review" element={<BookingReviewPage />} />
            <Route path="/hotel-booking-confirmation" element={<HotelBookingConfirmationPage />} />
            <Route path="/hotel-booking-confirmation/:bookingId" element={<HotelBookingConfirmationPage />} />
            <Route path="/hotel-cancel-review" element={<HotelCancellationReviewPage />} />
            <Route path="/train-results" element={<TrainResultsPage />} />
            <Route path="/train-results-page" element={<TrainResultsPage />} />
            <Route path="/yovo-trip-header" element={<YovoHeader />} />
            <Route path="/flight-search-header" element={<FlightSearchHeader />} />
            <Route path="/flight-fare-card" element={<FlightFareCard />} />
            <Route path="/flight_book" element={<FlightCard />} />
            <Route path="/flight-card" element={<FlightCardComponent />} />
            {/* <Route path="/yovo-trip-homepage" element={<Homepage2 />} /> */}
            <Route path="/flight" element={<FlightSelect />} />
            <Route path="/train" element={<TrainSelect />} />
            <Route path="/train-details" element={<TrainDetailsPage />} />
            <Route path="/train-schedule" element={<TrainSchedulePage />} />
            <Route path="/pnr-status" element={<PNRStatusPage />} />
            <Route path="/flight-search" element={<FlightSearchPage />} />
            <Route path="/flight-booking" element={<FlightBookingPage />} />
            {/* <Route path="/flight-results" element={<FlightResultsDemo />} /> */}
            <Route path="/flight-search-results" element={<FlightSearchResults />} />
            <Route path="/flight-search-results-demo" element={<FlightSearchResultsDemo />} />
            <Route path="/flight-details" element={<FlightDetails />} />
            <Route path="/flight-booking-page" element={<FlightBookingPageNew />} />
            <Route path="/seat-map-demo" element={<SeatMapDemo />} />
            <Route path="/seat-selection-demo" element={<SeatSelectionDemo />} />
            <Route path="/traveller-details" element={<TravellerDetails />} />
            <Route path="/flight-ssr-choose" element={<Flight_SSR_Choose />} />
            <Route path="/flight-preview" element={<Flight_Preview />} />
            <Route path="/payment" element={<PaymentPage />} />
            <Route path="/b/:code" element={<ShortLinkRedirect />} />
            <Route path="/booking-confirmation" element={<BookingConfirmation />} />
            <Route path="/view-booking-details" element={<ViewBookingDetails />} />
            <Route path="/ticket/:token" element={<TicketView />} />
            <Route path="/flight-cancellation" element={<FlightCancellation />} />
            <Route path="/cancellation-review" element={<CancellationReview />} />
            <Route path="/cancelled-booking-details" element={<CancelledBookingDetails />} />
            <Route path="/e-ticket" element={<ETicket />} />
            <Route path="/seat-selection" element={
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#F5F6FA', p: 3 }}>
                <SeatSelectionPage />
              </Box>
            } />
            <Route path="/cancellation-policy" element={
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, bgcolor: '#F5F6FA', minHeight: '100vh' }}>
                <CancellationPolicy />
              </Box>
            } />
            <Route path="/test-animation" element={<TestAnimation />} />
            <Route path="/destinations/:destinationSlug" element={<IstanbulGuide />} />
            <Route path="/destinations/heritage/:placeSlug" element={<HeritagePage />} />
            {/* Legacy URL redirects for SEO — keep old links working */}
            <Route path="/yovo_trip_guide" element={<Navigate to="/destinations/destination" replace />} />
            <Route path="/heritage-place/:placeName" element={<HeritageLegacyRedirect />} />
            <Route path="/my-trips" element={<MyTrips />} />
            <Route path="/account" element={<AccountPage />} />
            
            {/* Legal/Policy Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/user-agreement" element={<TermsOfService />} />
            <Route path="/terms-of-service" element={<Navigate to={POLICY_ROUTES.userAgreement} replace />} />
            <Route path="/cancellation-refund-policy" element={<CancellationRefundPolicy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faqs" element={<FAQPage />} />
            
            {/* Hotels Admin - Nested Routes */}
            <Route path="/admin/hotels" element={<AdminLayout section="hotels" />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<HotelFinancials embedded />} />
              <Route path="bookings" element={<HotelBookingsConsole embedded />} />
              <Route path="pricing" element={<HotelPricingGovernance embedded />} />
              {/* Phase UX Cleanup: Simulator and Compare now integrated into Pricing page */}
              <Route path="pricing/simulator" element={<Navigate to="/admin/hotels/pricing" replace />} />
              <Route path="pricing/compare" element={<Navigate to="/admin/hotels/pricing" replace />} />
              <Route path="recon" element={<HotelRecon embedded />} />
              {/* Phase 1: Hotel Offers */}
              <Route path="offers" element={<HotelOffers embedded />} />
            </Route>
            
            {/* Flights Admin - Nested Routes */}
            <Route path="/admin/flights" element={<AdminLayout section="flights" />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<FlightFinancials embedded />} />
              <Route path="bookings" element={<FlightBookingsConsole embedded />} />
              <Route path="pricing" element={<FlightPricingGovernance embedded />} />
              <Route path="service-fees" element={<ServiceFeeManagement />} />
              <Route path="recon" element={<FlightRecon embedded />} />
              <Route path="offers" element={<FlightOffers embedded />} />
            </Route>
            
            {/* Issue Monitor - Cross-cutting diagnostic page */}
            <Route path="/admin/issues" element={
              <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
                <IssueMonitorPage />
              </React.Suspense>
            } />
            
            {/* Refund Management - P0-2 Admin Refund Fallback */}
            <Route path="/admin/refunds" element={
              <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
                <RefundManagementPage />
              </React.Suspense>
            } />
            <Route path="/admin/service-requests" element={
              <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
                <ServiceRequestManagementPage />
              </React.Suspense>
            } />
            
            {/* Legacy Admin Route Redirects (backward compatibility) */}
            <Route path="/admin/financials" element={<Navigate to="/admin/flights/overview" replace />} />
            <Route path="/admin/hotel-financials" element={<Navigate to="/admin/hotels/overview" replace />} />
            <Route path="/admin/hotel-pricing" element={<Navigate to="/admin/hotels/pricing" replace />} />
            <Route path="/admin/hotel-pricing-simulator" element={<Navigate to="/admin/hotels/pricing" replace />} />
            <Route path="/admin/hotel-bookings" element={<Navigate to="/admin/hotels/bookings" replace />} />
            <Route path="/admin/hotels/pricing/governance" element={<Navigate to="/admin/hotels/pricing" replace />} />
            
            <Route path="/fare-plans" element={
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '100vh',
                bgcolor: '#F5F6FA',
                p: 3
              }}>
                <FlightDetailsAndFarePlans 
                  onClose={() => navigate('/')}
                  tripType="roundtrip"
                />
              </Box>
            } />
            {/* <Route path="/airport-search-demo" element={<AirportSearchDemo />} /> */}
            {/* Global 404 — must remain the last route so all explicit routes match first */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>

      {/* Chatbot - Compact Floating Window */}
      { shouldShowChatbot && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '380px',
          height: '480px',
          backgroundColor: '#F4F4F4',
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          border: '1px solid #E0E0E0',
          overflow: 'hidden'
        }}>
          <Chatbot open={shouldShowChatbot} setOpen={setIsChatbotOpen} flightData={flightData} />
        </div>
      )}

      {/* Floating Chatbot Button - Only show when chatbot is closed and not on homepage */}
      {showFloatingYovoBar && (
        <Box
          onClick={() => window.openChatbot && window.openChatbot()}
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            bgcolor: '#000',
            color: 'white',
            borderRadius: 0,
            px: 0.8,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            boxShadow: 'none',
            border: 'none',
            backdropFilter: 'blur(10px)',
            '&:hover': {
              bgcolor: '#333',
              transform: 'none',
              boxShadow: 'none',
            },
            transition: 'background-color 0.3s ease-in-out',
            zIndex: 1000,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: '24px', color: '#fff' }} />
          <Typography variant="body1" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Yovo Travel Guide
          </Typography>
        </Box>
      )}


    </>
  );
}

function App() {
  return (
    <HelmetProvider>
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
    <ThemeProvider>
      <AuthProvider>
      <SearchProvider>
        <Router>
          <AppContent />
        </Router>
      </SearchProvider>
      </AuthProvider>
    </ThemeProvider>
    </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export default App;
